import { Leaves } from "./definition";
import { ValueOrEnumControl } from "./extract-styles-values";
import {
    AnyControl,
    AnyDecoration,
    AnyValueOrEnumControl,
    BlockDefinition,
    BlockStyles,
    FontFamilyControl,
    PaginationSettings,
    Style,
    StyleGroup,
} from "./types";
import { WindowUser } from "./types/window";

/**
 * Get value from object by path.
 *
 * Simplified version of `lodash.get`. Does not support array notation by design.
 */
export const get = (obj: unknown, path: string) => {
    const parts = path.split(".");
    return parts.reduce((prev, curr) => (prev as Record<string, unknown> | undefined)?.[curr], obj);
};

/**
 * Set object value at path. This function mutates object in place.
 *
 * Simplified version of `lodash.set`. Does not support array notation by design.
 */
export const set = (obj: Record<string, unknown>, path: string, value: unknown) => {
    const pathArray = path.split(".");

    pathArray.reduce((acc, key, i) => {
        if (acc[key] === undefined) {
            acc[key] = {};
        }

        if (i === pathArray.length - 1) {
            acc[key] = value;
        }

        return acc[key] as Record<string, unknown>;
    }, obj);
};

export const isStyleGroup = (style: Style): style is StyleGroup => style.type === "group";
export const isControl = (style: Style): style is AnyControl => style.type === "control";
export const isDecoration = (style: Style): style is AnyDecoration => style.type === "decoration";

export const isValueOrEnumControl = (control: AnyControl): control is AnyValueOrEnumControl => "value" in control;

export const isFontFamilyControl = (control: AnyControl): control is FontFamilyControl =>
    control.controlType === "fontFamily";

export const findAllControls = (styles: ReadonlyArray<Style>): AnyValueOrEnumControl[] => {
    if (!styles) return [];
    return styles.flatMap((style) => {
        if (isDecoration(style)) {
            return [];
        }
        if (isStyleGroup(style)) {
            return findAllControls(style.styles);
        }

        if (isValueOrEnumControl(style)) {
            return style;
        }

        return findAllControls(style.styles);
    });
};

export const countDuplicateControlKeys = (styles: ReadonlyArray<Style>) => {
    const controls = findAllControls(styles).filter(isValueOrEnumControl);

    const counts: Record<string, number> = {};
    controls.forEach((control) => {
        counts[control.key] = (counts[control.key] || 0) + 1;
    });

    return Object.entries(counts).filter(([, count]) => count > 1);
};

export const resolveChildControls = (parentControls: AnyValueOrEnumControl[], childControls: AnyValueOrEnumControl[]) =>
    childControls.filter((childControl) => {
        const parentControlIndex = parentControls.findIndex((parentControl) => parentControl.key === childControl.key);
        return parentControlIndex === -1 || childControl.value !== null;
    });

/**
 * Find a style group by key.
 * Recursively visits child groups.
 */
export function findStyleGroupByKey(styles: readonly Style[], key: string): StyleGroup | undefined {
    return find(styles);

    function find(style: Style | readonly Style[]): StyleGroup | undefined {
        if (Array.isArray(style)) {
            for (const child of style) {
                const found = find(child);
                if (found) {
                    return found;
                }
            }
            return undefined;
        } else {
            // isArray narrowing doesn't work here
            // because the type is readonly Style[], instead of Style[]
            // This case is safe.
            const s = style as Style;
            if (s.type !== "group") {
                return undefined;
            }
            if (s.key === key) {
                return s;
            }
            if (s.type === "group") {
                // recursively visit children
                return find(s.styles);
            }
            return undefined;
        }
    }
}
export function findStyleByKey<S extends BlockStyles = BlockStyles>(
    styles: readonly Style[],
    key: Leaves<S, BlockStyles, 3>,
): ValueOrEnumControl {
    const found = find(styles);
    if (!found) throw new Error(`Style with key ${key} not found`);
    if (found && !isValueOrEnumControl(found as never)) throw new Error(`Style with key ${key} is not a control`);
    return found as never;

    function find(style: Style | readonly Style[]): Style | undefined {
        if (Array.isArray(style)) {
            for (const child of style) {
                const found = find(child);
                if (found) return found;
            }
            return undefined;
        } else {
            if ("styles" in style) {
                // recursively visit children
                return find(style.styles);
            } else {
                if ("key" in style && style.key === key) {
                    return style;
                } else {
                    return undefined;
                }
            }
        }
    }
}
export function findStyleByKeyOrNull<S extends BlockStyles = BlockStyles>(
    styles: readonly Style[],
    key: Leaves<S, BlockStyles, 3>,
): ValueOrEnumControl | null {
    const found = find(styles);
    if (!found) return null;
    if (found && !isValueOrEnumControl(found as never)) throw new Error(`Style with key ${key} is not a control`);
    return found as never;

    function find(style: Style | readonly Style[]): Style | null {
        if (Array.isArray(style)) {
            for (const child of style) {
                const found = find(child);
                if (found) return found;
            }
            return null;
        } else {
            if ("styles" in style) {
                // recursively visit children
                return find(style.styles);
            } else {
                if ("key" in style && style.key === key) {
                    return style;
                } else {
                    return null;
                }
            }
        }
    }
}

/**
 * This function extracts all styles from a block.
 *
 * NOTE: When called for blocks older than v5, this function will return an
 * empty array.
 */
export const extractStylesFromBlock = (block: Pick<BlockDefinition, "elements">): Style[] => {
    const containerStyles: ReadonlyArray<Style> = block.elements.container?.styles ?? [];

    const fieldStyles: ReadonlyArray<Style> = (block.elements.fields?.items ?? []).flatMap(
        (field) => field.styles ?? [],
    );

    /**
     * Conditional form blocks have sections and each section contains fields,
     * which can have styles.
     */
    const formSectionStyles: ReadonlyArray<Style> = (block.elements.form?.steps ?? [])
        .flatMap((step) => step.sections ?? [])
        .flatMap((section) => section.fields ?? [])
        .flatMap((field) => field.styles ?? []);

    return [...containerStyles, ...fieldStyles, ...formSectionStyles];
};

/** This comparison utility assumes that the following conditions will remain true for style values:
 * - String values comparison should be case-insensitive, for example, '#ffffff' and '#FFFFFF' is the style value. This applies to any
 * string value, not only to colors.
 * - Style value is always a primitive.
 */
export const styleValuesAreEqual = (a: unknown, b: unknown): boolean => {
    if (isString(a) && isString(b)) {
        return a.toLowerCase() === b.toLowerCase();
    }
    return a === b;
};

export const isString = (value: unknown): value is string => {
    return typeof value === "string";
};

export const substituteMarkupWithUserPlaceholders = (
    value: string | null,
    loggedInUser: WindowUser | undefined,
    encode: boolean = true,
): string => {
    const LOGGED_IN_USER_EMAIL = "{LOGGED_IN_USER:EMAIL}";
    const LOGGED_IN_USER_NAME = "{LOGGED_IN_USER:NAME}";
    const LOGGED_IN_USER_RECORD_ID = "{LOGGED_IN_USER_RECORD_ID}";
    const LOGGED_IN_USER_FIELD = "{LOGGED_IN_USER_FIELD:";

    if (!value) return "";
    if (!loggedInUser) return value;

    const replacements = {
        [LOGGED_IN_USER_EMAIL]: loggedInUser.softr_user_email || "",
        [LOGGED_IN_USER_NAME]: loggedInUser.softr_user_full_name || "",
        [LOGGED_IN_USER_RECORD_ID]: loggedInUser.record_id || "",
    };

    let updatedValue = value;

    /** Replace static placeholders */
    for (const [placeholder, value] of Object.entries(replacements)) {
        const replacementValue = encode ? encodeURIComponent(String(value)) : value;
        updatedValue = updatedValue.replaceAll(placeholder, String(replacementValue));
    }

    /** Replace dynamic placeholders
     If url is https://example.com/user/{LOGGED_IN_USER_FIELD:address},
     the regular expression will match {LOGGED_IN_USER_FIELD:address} and get address part.
     */
    const dynamicPlaceholderRegex = new RegExp(`${LOGGED_IN_USER_FIELD}(.*?)}`, "g");

    updatedValue = updatedValue.replace(dynamicPlaceholderRegex, (_match: string, value) => {
        if (!loggedInUser[value]) {
            return "";
        }
        return encode ? encodeURIComponent(String(loggedInUser[value])) : String(loggedInUser[value]);
    });

    return updatedValue;
};

/**
 * returns a new string where all forward slashes (/) are replaced with escaped forward slashes (\\/).
 *
 * The reason is the block name, when we try to get the block in an HTML
 * document.querySelector(`#${hrid} section[style*="display: none;"]`);
 * as the block name is block/title it is not a valid selector for the document.querySelector.
 *
 *  block\/title output example.
 * */
export const escapeSlashes = (id: string): string => {
    return id.replace(/\//g, "\\/");
};

/**
 * Picks a stable option from a list of options based on a unique identifier.
 * This is useful for ensuring that the same placeholder is used for a given record.
 */
export function pickStableOption<T extends unknown[]>(options: T, uid: string): T[number] {
    const index = hashStr(uid) % options.length;
    return options[index];
}

/**
 * Hashes a string to a number.
 *
 * Output is a positive integer as long as you don't feed in absurdly long input (~<130M characters)
 */
export function hashStr(content: string) {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
        hash += content.charCodeAt(i);
    }
    return hash;
}

export function createPaginationSettings(): PaginationSettings {
    return {
        method: {
            type: "elementOptionRowContainer",
            label: "",
            options: [
                {
                    type: "select",
                    options: ["NONE", "HORIZONTAL_SCROLL", "PAGINATION"],
                    optionsMeta: {
                        PAGINATION: { label: "Pages" },
                        HORIZONTAL_SCROLL: { label: "Horizontal scroll" },
                        NONE: { label: "None" },
                        /* INFINITE_SCROLL is not supported for the list block */
                        INFINITE_SCROLL: { label: "Infinity scroll", hidden: true },
                    },
                    label: "Method",
                    value: "PAGINATION",
                },
                {
                    type: "number",
                    label: "",
                    value: 12,
                    required: true,
                },
            ],
        },
        hideOverflowingCards: {
            type: "boolean",
            label: "Hide overflowing cards",
            info: "Arrows will show to indicate overflowing cards.",
            value: true,
        },
    };
}
